/**
 * MoleculeViewer3D - Enhanced 3D molecular structure viewer component
 *
 * Features:
 * - Multiple visualization styles (cartoon, stick, sphere, surface)
 * - Color schemes including pLDDT for AlphaFold structures
 * - Domain/region highlighting on protein structures
 * - Interactive controls (rotate, zoom, pan)
 * - Residue selection and clicking
 * - Labels for domains
 * - Screenshot export
 *
 * Uses 3Dmol.js (https://3dmol.csb.pitt.edu/) for WebGL-based rendering
 */

import { useEffect, useRef, useState, useCallback, Component, type ErrorInfo, type ReactNode } from 'react'
import { Loader2, RotateCcw, Camera, Eye, EyeOff, Layers, Target, Puzzle } from 'lucide-react'
import { Button } from './ui/Button'
import {
  use3DmolViewer,
  COLOR_SCHEMES,
  DOMAIN_COLORS,
  type DomainHighlight,
  type ResidueSelection,
  type SiteHighlight,
} from '../hooks/use3DmolViewer'

/**
 * Visualization style options
 */
export type VisualizationStyle = 'cartoon' | 'stick' | 'sphere' | 'surface'

/**
 * Color scheme options
 */
export type ColorScheme =
  | 'spectrum'
  | 'chain'
  | 'secondary'
  | 'hydrophobicity'
  | 'amino'
  | 'plddt'
  | 'domains'

/**
 * Domain data for highlighting
 */
export interface DomainData {
  /** Domain name/label */
  name: string
  /** Start residue number */
  resStart: number
  /** End residue number */
  resEnd: number
  /** Optional color (uses DOMAIN_COLORS if not provided) */
  color?: string
  /** Optional chain ID */
  chain?: string
}

/**
 * Functional site data for highlighting
 */
export interface SiteData {
  /** Site type (e.g., "active site", "binding site") */
  siteType: string
  /** Residue positions as string (e.g., "A42, A45, A89") */
  residues: string
  /** Source domain (optional) */
  sourceDomain?: string
  /** Optional color (default: magenta) */
  color?: string
}

/**
 * PAE structural domain data for highlighting
 * (Predicted Aligned Error domains from ColabFold)
 */
export interface PaeDomainData {
  /** Domain ID */
  domainId: number
  /** Start residue number */
  resStart: number
  /** End residue number */
  resEnd: number
  /** Optional color (uses predefined colors if not provided) */
  color?: string
}

/**
 * Props for MoleculeViewer3D component
 */
export interface MoleculeViewer3DProps {
  /** PDB data as string */
  pdbData?: string
  /** URL to fetch PDB file from */
  pdbUrl?: string
  /** Molecular file format (default: 'pdb') */
  format?: string
  /** Container height (default: '500px') */
  height?: string
  /** Container width (default: '100%') */
  width?: string

  /** Initial visualization style */
  style?: VisualizationStyle
  /** Initial color scheme */
  colorScheme?: ColorScheme
  /** Background color */
  backgroundColor?: string

  /** Domains to highlight on the structure */
  domains?: DomainData[]
  /** Show domain labels on the 3D structure */
  showDomainLabels?: boolean
  /** Base color scheme when domains are highlighted */
  domainBaseColorScheme?: keyof typeof COLOR_SCHEMES

  /** Functional sites to highlight as stick representation */
  sites?: SiteData[]
  /** Show sites as sticks by default */
  showSitesByDefault?: boolean

  /** PAE structural domains to highlight (ColabFold) */
  paeDomains?: PaeDomainData[]
  /** Show PAE domains by default */
  showPaeDomainsByDefault?: boolean

  /** Show controls UI */
  showControls?: boolean
  /** Show instructions */
  showInstructions?: boolean

  /** Callback when residue is clicked */
  onResidueClick?: (residue: ResidueSelection) => void
  /** Callback when structure is loaded */
  onLoad?: () => void
  /** Callback on error */
  onError?: (error: Error) => void

  /** Additional CSS class */
  className?: string
}

/**
 * Color scheme labels for UI
 */
const COLOR_SCHEME_LABELS: Record<ColorScheme, string> = {
  spectrum: 'Rainbow',
  chain: 'By Chain',
  secondary: 'Secondary Structure',
  hydrophobicity: 'Hydrophobicity',
  amino: 'Amino Acid Type',
  plddt: 'pLDDT Confidence',
  domains: 'Domains',
}

/**
 * Style labels for UI
 */
const STYLE_LABELS: Record<VisualizationStyle, string> = {
  cartoon: 'Cartoon',
  stick: 'Stick',
  sphere: 'Sphere',
  surface: 'Surface',
}

// Re-export for convenience
export { COLOR_SCHEMES, DOMAIN_COLORS, type DomainHighlight, type ResidueSelection, type SiteHighlight }

/**
 * MoleculeViewer3D Component
 */
export function MoleculeViewer3D({
  pdbData,
  pdbUrl,
  format = 'pdb',
  height = '500px',
  width = '100%',
  style: initialStyle = 'cartoon',
  colorScheme: initialColorScheme = 'spectrum',
  backgroundColor = 'rgba(0,0,0,0)',
  domains,
  showDomainLabels = false,
  domainBaseColorScheme = 'secondary',
  sites,
  showSitesByDefault = false,
  paeDomains,
  showPaeDomainsByDefault = false,
  showControls = true,
  showInstructions = true,
  onResidueClick,
  onLoad,
  onError,
  className = '',
}: MoleculeViewer3DProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [currentStyle, setCurrentStyle] = useState<VisualizationStyle>(initialStyle)
  const [currentColorScheme, setCurrentColorScheme] = useState<ColorScheme>(initialColorScheme)
  const [showDomainsHighlight, setShowDomainsHighlight] = useState(!!domains)
  const [showSitesHighlight, setShowSitesHighlight] = useState(showSitesByDefault)
  const [showPaeDomainsHighlight, setShowPaeDomainsHighlight] = useState(showPaeDomainsByDefault)
  const [structureLoaded, setStructureLoaded] = useState(false)

  // Use the custom 3Dmol hook
  const {
    isReady,
    isLoading: viewerLoading,
    error: viewerError,
    loadPDB,
    loadFromUrl,
    applyCartoonStyle,
    applyStickStyle,
    applySphereStyle,
    addSurface,
    removeSurfaces,
    highlightDomains,
    highlightSites,
    colorByPLDDT,
    addDomainLabels,
    clearLabels,
    resetView,
    render,
    downloadScreenshot,
  } = use3DmolViewer(containerRef, {
    backgroundColor,
    antialias: true,
    onResidueClick,
  })

  // Prepare domain highlights with colors (inline, no useMemo)
  const domainHighlights: DomainHighlight[] = domains
    ? domains.map((domain, idx) => ({
        name: domain.name,
        resStart: domain.resStart,
        resEnd: domain.resEnd,
        color: domain.color || DOMAIN_COLORS[idx % DOMAIN_COLORS.length],
        chain: domain.chain,
      }))
    : []

  // Parse site residues from string format (e.g., "A42, A45, A89" or "42, 45, 89")
  const parseResidueString = (residueStr: string): number[] => {
    if (!residueStr) return []
    return residueStr
      .split(/[,\s]+/)
      .map(s => s.trim())
      .filter(s => s.length > 0)
      .map(s => {
        // Extract number, ignoring any chain prefix like "A42" -> 42
        const match = s.match(/\d+/)
        return match ? parseInt(match[0], 10) : NaN
      })
      .filter(n => !isNaN(n))
  }

  // Prepare site highlights with parsed residues
  const siteHighlights: SiteHighlight[] = sites
    ? sites.map((site) => ({
        siteType: site.siteType,
        residues: parseResidueString(site.residues),
        sourceDomain: site.sourceDomain,
        color: site.color || '#FF00FF', // Magenta default
      }))
    : []

  // PAE domain colors (distinct from conserved domain colors)
  const PAE_DOMAIN_COLORS = [
    '#FF9500', // Orange
    '#00C7BE', // Teal
    '#AF52DE', // Purple
    '#FF2D55', // Pink
    '#5AC8FA', // Light Blue
    '#FFCC00', // Yellow
    '#30D158', // Green
    '#FF6482', // Coral
  ]

  // Prepare PAE domain highlights
  const paeDomainHighlights: DomainHighlight[] = paeDomains
    ? paeDomains.map((pae, idx) => ({
        name: `PAE Domain ${pae.domainId}`,
        resStart: pae.resStart,
        resEnd: pae.resEnd,
        color: pae.color || PAE_DOMAIN_COLORS[idx % PAE_DOMAIN_COLORS.length],
      }))
    : []

  // Load structure when data changes
  useEffect(() => {
    if (!isReady) return

    const loadStructure = async () => {
      let success = false

      if (pdbData) {
        success = loadPDB(pdbData, format)
      } else if (pdbUrl) {
        success = await loadFromUrl(pdbUrl, format)
      }

      if (success) {
        setStructureLoaded(true)
        onLoad?.()
      }
    }

    loadStructure()
  }, [isReady, pdbData, pdbUrl, format, loadPDB, loadFromUrl, onLoad])

  // Apply styles when structure is loaded or settings change
  useEffect(() => {
    if (!structureLoaded) return

    // Remove any existing surfaces
    removeSurfaces()

    // Apply visualization style
    if (currentStyle === 'cartoon') {
      if (showDomainsHighlight && domainHighlights.length > 0) {
        highlightDomains(domainHighlights, domainBaseColorScheme)
        if (showDomainLabels) {
          addDomainLabels(domainHighlights)
        } else {
          clearLabels()
        }
      } else if (currentColorScheme === 'plddt') {
        colorByPLDDT()
        clearLabels()
      } else if (currentColorScheme === 'domains' && domainHighlights.length > 0) {
        highlightDomains(domainHighlights, domainBaseColorScheme)
        if (showDomainLabels) {
          addDomainLabels(domainHighlights)
        }
      } else {
        applyCartoonStyle(currentColorScheme as keyof typeof COLOR_SCHEMES)
        clearLabels()
      }
    } else if (currentStyle === 'stick') {
      applyStickStyle(0.15)
      clearLabels()
    } else if (currentStyle === 'sphere') {
      applySphereStyle(0.5)
      clearLabels()
    } else if (currentStyle === 'surface') {
      applyCartoonStyle(currentColorScheme as keyof typeof COLOR_SCHEMES)
      addSurface('VDW', 0.7)
      clearLabels()
    }

    // Apply site highlighting as stick overlay (only in cartoon mode)
    if (showSitesHighlight && siteHighlights.length > 0 && currentStyle === 'cartoon') {
      highlightSites(siteHighlights, true)
    }

    // Apply PAE domain highlighting (only in cartoon mode, after main style)
    if (showPaeDomainsHighlight && paeDomainHighlights.length > 0 && currentStyle === 'cartoon') {
      // PAE domains are highlighted like conserved domains
      highlightDomains(paeDomainHighlights, domainBaseColorScheme)
    }

    render()
    resetView()
  }, [
    structureLoaded,
    currentStyle,
    currentColorScheme,
    showDomainsHighlight,
    domainHighlights,
    showDomainLabels,
    domainBaseColorScheme,
    showSitesHighlight,
    siteHighlights,
    showPaeDomainsHighlight,
    paeDomainHighlights,
    applyCartoonStyle,
    applyStickStyle,
    applySphereStyle,
    addSurface,
    removeSurfaces,
    highlightDomains,
    highlightSites,
    colorByPLDDT,
    addDomainLabels,
    clearLabels,
    render,
    resetView,
  ])

  // Handle errors
  useEffect(() => {
    if (viewerError) {
      onError?.(viewerError)
    }
  }, [viewerError, onError])

  // Handle style change
  const handleStyleChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setCurrentStyle(e.target.value as VisualizationStyle)
  }, [])

  // Handle color scheme change
  const handleColorSchemeChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const scheme = e.target.value as ColorScheme
    setCurrentColorScheme(scheme)
    if (scheme === 'domains') {
      setShowDomainsHighlight(true)
    }
  }, [])

  // Toggle domain highlighting
  const toggleDomainHighlight = useCallback(() => {
    setShowDomainsHighlight((prev) => !prev)
  }, [])

  // Toggle site highlighting
  const toggleSiteHighlight = useCallback(() => {
    setShowSitesHighlight((prev) => !prev)
  }, [])

  // Toggle PAE domain highlighting
  const togglePaeDomainHighlight = useCallback(() => {
    setShowPaeDomainsHighlight((prev) => !prev)
  }, [])

  // Handle reset
  const handleReset = useCallback(() => {
    resetView()
  }, [resetView])

  // Handle screenshot
  const handleScreenshot = useCallback(() => {
    downloadScreenshot('protein_structure.png')
  }, [downloadScreenshot])

  // Determine if loading
  const isLoading = viewerLoading || (!structureLoaded && (!!pdbData || !!pdbUrl))

  // Get available color schemes (include domains if domains are provided)
  const availableColorSchemes: Partial<Record<ColorScheme, string>> = { ...COLOR_SCHEME_LABELS }
  if (!domains || domains.length === 0) {
    delete availableColorSchemes.domains
  }

  return (
    <div className={`relative ${className}`} style={{ width, height }}>
      {/* Controls - Style on left, Actions & Toggles on right */}
      {showControls && (
        <>
          {/* Left: Style controls */}
          <div className="absolute top-4 left-4 z-10">
            <div className="flex items-center gap-1 bg-dark-bg-primary/80 backdrop-blur rounded-lg p-1.5 border border-white/10">
              <select
                value={currentStyle}
                onChange={handleStyleChange}
                className="px-2 py-1 text-xs bg-transparent border-none text-white cursor-pointer hover:bg-white/10 rounded transition-colors focus:outline-none"
                aria-label="Visualization style"
              >
                {Object.entries(STYLE_LABELS).map(([value, label]) => (
                  <option key={value} value={value} className="bg-dark-bg-primary text-white" style={{ backgroundColor: '#1a1a24', color: '#f3f4f6' }}>
                    {label}
                  </option>
                ))}
              </select>
              <div className="w-px h-5 bg-white/20" />
              <select
                value={currentColorScheme}
                onChange={handleColorSchemeChange}
                className="px-2 py-1 text-xs bg-transparent border-none text-white cursor-pointer hover:bg-white/10 rounded transition-colors focus:outline-none"
                aria-label="Color scheme"
              >
                {Object.entries(availableColorSchemes).map(([value, label]) => (
                  <option key={value} value={value} className="bg-dark-bg-primary text-white" style={{ backgroundColor: '#1a1a24', color: '#f3f4f6' }}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Right: Action buttons & Domain toggles */}
          <div className="absolute top-4 right-4 z-10 flex flex-col gap-2">
            {/* Action buttons */}
            <div className="flex items-center gap-1 bg-dark-bg-primary/80 backdrop-blur rounded-lg p-1.5 border border-white/10">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleReset}
                leftIcon={<RotateCcw className="w-3.5 h-3.5" />}
                className="hover:bg-white/10 text-xs px-2 py-1"
              >
                Reset
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleScreenshot}
                leftIcon={<Camera className="w-3.5 h-3.5" />}
                className="hover:bg-white/10 text-xs px-2 py-1"
              >
                Screenshot
              </Button>
            </div>

            {/* Domain/Feature toggles (only if any toggles are available) */}
            {currentStyle === 'cartoon' && (
              (domains && domains.length > 0) ||
              (sites && sites.length > 0) ||
              (paeDomains && paeDomains.length > 0)
            ) && (
              <div className="flex flex-col gap-1 bg-dark-bg-primary/80 backdrop-blur rounded-lg p-1.5 border border-white/10">
                {/* Domain toggle button */}
                {domains && domains.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={toggleDomainHighlight}
                    leftIcon={showDomainsHighlight ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                    className={`text-xs px-2 py-1 justify-start ${showDomainsHighlight ? 'text-accent-cyan bg-accent-cyan/20' : 'hover:bg-white/10'}`}
                  >
                    Domains
                  </Button>
                )}

                {/* Sites toggle button */}
                {sites && sites.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={toggleSiteHighlight}
                    leftIcon={<Target className="w-3.5 h-3.5" />}
                    className={`text-xs px-2 py-1 justify-start ${showSitesHighlight ? 'text-accent-purple bg-accent-purple/20' : 'hover:bg-white/10'}`}
                  >
                    Sites
                  </Button>
                )}

                {/* PAE Domains toggle button */}
                {paeDomains && paeDomains.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={togglePaeDomainHighlight}
                    leftIcon={<Puzzle className="w-3.5 h-3.5" />}
                    className={`text-xs px-2 py-1 justify-start ${showPaeDomainsHighlight ? 'text-accent-amber bg-accent-amber/20' : 'hover:bg-white/10'}`}
                  >
                    PAE Domains
                  </Button>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* pLDDT color legend (show when pLDDT color scheme is active) */}
      {currentColorScheme === 'plddt' && structureLoaded && (
        <div className="absolute bottom-12 right-4 z-10 bg-dark-bg-primary/80 backdrop-blur border border-white/10 rounded-lg p-3">
          <div className="text-xs text-gray-400 mb-2 font-medium">pLDDT Confidence</div>
          <div className="flex flex-col gap-1 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-4 h-3 rounded" style={{ backgroundColor: '#0D56D3' }} />
              <span className="text-gray-300">Very High (&gt;90)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-3 rounded" style={{ backgroundColor: '#6ACBF1' }} />
              <span className="text-gray-300">Confident (70-90)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-3 rounded" style={{ backgroundColor: '#FED936' }} />
              <span className="text-gray-300">Low (50-70)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-3 rounded" style={{ backgroundColor: '#FD7D4D' }} />
              <span className="text-gray-300">Very Low (&lt;50)</span>
            </div>
          </div>
        </div>
      )}

      {/* Domain legend (show when domains are highlighted) */}
      {showDomainsHighlight && domainHighlights.length > 0 && structureLoaded && (
        <div className="absolute bottom-12 left-4 z-10 bg-dark-bg-primary/80 backdrop-blur border border-white/10 rounded-lg p-3 max-w-xs">
          <div className="text-xs text-gray-400 mb-2 font-medium flex items-center gap-1">
            <Layers className="w-3 h-3" />
            Domains
          </div>
          <div className="flex flex-col gap-1 text-xs max-h-32 overflow-y-auto">
            {domainHighlights.map((domain, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <div
                  className="w-4 h-3 rounded flex-shrink-0"
                  style={{ backgroundColor: domain.color }}
                />
                <span className="text-gray-300 truncate" title={domain.name}>
                  {domain.name} ({domain.resStart}-{domain.resEnd})
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sites legend (show when sites are highlighted) */}
      {showSitesHighlight && siteHighlights.length > 0 && structureLoaded && (
        <div className="absolute bottom-12 left-4 z-10 bg-dark-bg-primary/80 backdrop-blur border border-accent-purple/30 rounded-lg p-3 max-w-xs" style={{ left: showDomainsHighlight && domainHighlights.length > 0 ? '200px' : '16px' }}>
          <div className="text-xs text-gray-400 mb-2 font-medium flex items-center gap-1">
            <Target className="w-3 h-3 text-accent-purple" />
            Functional Sites (sticks)
          </div>
          <div className="flex flex-col gap-1 text-xs max-h-32 overflow-y-auto">
            {siteHighlights.map((site, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <div
                  className="w-4 h-3 rounded flex-shrink-0"
                  style={{ backgroundColor: site.color }}
                />
                <span className="text-gray-300 truncate" title={`${site.siteType}: ${site.residues.join(', ')}`}>
                  {site.siteType} ({site.residues.length} residues)
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* PAE Domains legend (show when PAE domains are highlighted) */}
      {showPaeDomainsHighlight && paeDomainHighlights.length > 0 && structureLoaded && (
        <div className="absolute bottom-12 right-4 z-10 bg-dark-bg-primary/80 backdrop-blur border border-accent-amber/30 rounded-lg p-3 max-w-xs">
          <div className="text-xs text-gray-400 mb-2 font-medium flex items-center gap-1">
            <Puzzle className="w-3 h-3 text-accent-amber" />
            PAE Structural Domains
          </div>
          <div className="flex flex-col gap-1 text-xs max-h-32 overflow-y-auto">
            {paeDomainHighlights.map((pae, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <div
                  className="w-4 h-3 rounded flex-shrink-0"
                  style={{ backgroundColor: pae.color }}
                />
                <span className="text-gray-300 truncate" title={pae.name}>
                  {pae.name} ({pae.resStart}-{pae.resEnd})
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Viewer container */}
      <div
        ref={containerRef}
        className="w-full h-full rounded-xl bg-dark-bg-secondary border border-white/10 overflow-hidden"
      />

      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-dark-bg-secondary/80 rounded-xl">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-accent-cyan" />
            <p className="text-gray-400">Loading structure...</p>
          </div>
        </div>
      )}

      {/* Error overlay */}
      {viewerError && (
        <div className="absolute inset-0 flex items-center justify-center bg-dark-bg-secondary/80 rounded-xl">
          <div className="text-center p-4">
            <p className="text-red-400 mb-4">{viewerError.message}</p>
            <Button variant="secondary" onClick={() => window.location.reload()}>
              Retry
            </Button>
          </div>
        </div>
      )}

      {/* No data message */}
      {!isLoading && !viewerError && !pdbData && !pdbUrl && (
        <div className="absolute inset-0 flex items-center justify-center bg-dark-bg-secondary/80 rounded-xl">
          <p className="text-gray-500">No structure data provided</p>
        </div>
      )}

      {/* Instructions */}
      {showInstructions && structureLoaded && (
        <div className="absolute bottom-4 left-4 text-xs text-gray-500">
          Drag to rotate • Scroll to zoom • Shift+drag to translate
        </div>
      )}
    </div>
  )
}

// Error Boundary to catch and display errors
interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error?: Error
}

class MoleculeViewerErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('MoleculeViewer3D Error:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div className="h-full flex items-center justify-center bg-dark-bg-secondary rounded-xl p-4">
          <div className="text-center">
            <p className="text-red-400 mb-2">Failed to load 3D viewer</p>
            <p className="text-gray-500 text-sm">{this.state.error?.message}</p>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

// Wrapped export with error boundary
export function MoleculeViewer3DWithErrorBoundary(props: MoleculeViewer3DProps) {
  return (
    <MoleculeViewerErrorBoundary>
      <MoleculeViewer3D {...props} />
    </MoleculeViewerErrorBoundary>
  )
}

export default MoleculeViewer3D
